import React, { FC, useEffect, useRef, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useSelector, useDispatch } from 'react-redux'
import { useHistory, useParams } from 'react-router-dom'

import { EuiButton, EuiButtonIcon, EuiEmptyPrompt, EuiFlexGroup, EuiFlexItem, EuiHorizontalRule, EuiLoadingContent, EuiPage, EuiPageBody, EuiPanel, EuiSpacer, EuiTabbedContent, EuiTitle, EuiToolTip } from '@elastic/eui'
import { EuiTabbedContentTab } from '@elastic/eui/src/components/tabs/tabbed_content/tabbed_content'

import { AIEditFlyoutState } from 'api/interfaces/AIEditTypes'
import { MarketAnalysisCustomSectionPosition } from 'api/interfaces/AIPlanMarketAnalysisSection'
import { CompetitorProfile, GeographicCluster, MarketChallenge } from 'api/interfaces/MarketAnalysis'
import { useGetBillboardsByZipCodesQuery } from 'api/rtkQueryApi/platform/billboardApi'
import {
  useGetAIPlanQuery,
  useGetAIPlanStubQuery,
  useGetAIPlanExecutiveSummarySectionQuery,
  useGetAIPlanPersonasSectionQuery,
  useAiPlanEditNameMutation,
  useAiPlanEditMarketAnalysisSectionMutation,
  useAddAIPlanCompetitorProfileMutation,
  useEditAIPlanCompetitorProfileMutation,
  useRemoveAIPlanCompetitorProfileMutation,
  useAddAIPlanGeographicClusterMutation,
  useEditAIPlanGeographicClusterMutation,
  useRemoveAIPlanGeographicClusterMutation,
  useAddAIPlanMarketChallengeMutation,
  useEditAIPlanMarketChallengeMutation,
  useRemoveAIPlanMarketChallengeMutation,
  useAiEditAIPlanMutation,
  useGetAIPlanMarketAnalysisSectionQuery,
  useUpdateAIPlanMarketAnalysisSectionTitleMutation,
  useAddAIPlanMarketAnalysisCustomSectionMutation,
  useUpdateAIPlanMarketAnalysisCustomSectionMutation,
  useDeleteAIPlanMarketAnalysisCustomSectionMutation,
  useUpdateAIPlanMarketAnalysisIntroductionSectionMutation,
  useHideAIPlanMarketAnalysisIntroductionSectionMutation,
  useCreateAIPlanPublicIdMutation,
  useUpdateAIPlanClientPreviewTabsMutation
} from 'api/rtkQueryApi/platform/aiPlansApi'
import config from 'app/config'
import { RootState } from 'app/rootReducer'
import { AcConfirmModal } from 'components/Modals/AcConfirmModal'
import MultiTabPdfProgressModal from 'components/MultiTabPdfProgressModal'
import AIEditPromptFlyout from 'features/planner/components/AIEditPromptFlyout'
import AIPlannerMarkdownRenderer from 'features/planner/components/AIPlannerMarkdownRenderer'
import EditTitleFlyout from 'features/planner/components/EditTitleFlyout'
import { STAFF_PDF_CENSUS_UNAVAILABLE_TOOLTIP, useHouseholdCensusData } from 'features/planner/hooks/useHouseholdCensusData'
import { marketAnalysisToMarkdown } from 'features/planner/components/StructuredMarketAnalysis/marketAnalysisToMarkdown'
import StructuredMarketAnalysis from 'features/planner/components/StructuredMarketAnalysis/StructuredMarketAnalysis'
import MarketAnalysisMarkdownRenderer from 'features/planner/pages/MasterCampaignPlanPage/components/MarketAnalysisMarkdownRenderer'
import EditCustomSectionFlyout, { MARKET_ANALYSIS_CUSTOM_SECTION_EXAMPLE_CONTENT } from 'features/planner/pages/MasterCampaignPlanPage/components/ExecutiveSummary/EditCustomSectionFlyout'
import PageContent from 'features/planner/pages/MasterCampaignPlanPage/components/PageContent'
import { useAIEdit } from 'hooks/useAIEdit'
import { useAIPlanCombinedPdfGeneration } from 'hooks/useAIPlanCombinedPdfGeneration'
import { generateAIPlanMarketAnalysisPdf, AIPlanPdfGenerationContext } from 'services/aiPlanTabPdfGenerators'

import './AIPlanPage.scss'

import { resetEditMode, selectAIPlanEditMode } from './aiPlanSlice'
import BrandingUploadModal, { BrandingImages } from './components/BrandingUploadModal'
import AIPlanExecutiveSummary from './components/AIPlanExecutiveSummary'
import AIPlanHeader from './components/AIPlanHeader'
import EditAIPlanNameFlyout from './components/EditAIPlanNameFlyout'
import AIPlanMediaPlan, { AIPlanMediaPlanRef } from './components/MediaPlan/AIPlanMediaPlan'
import AIPlanPersonas from './components/Personas/AIPlanPersonas'
import AIPlanClientPreviewTabsFlyout from './components/AIPlanClientPreviewTabsFlyout'
import AIPlanProjections from './components/Projections/AIPlanProjections'
import { useAIPlanBillboardZipCodes, useAIPlanHouseholdZipCodes } from './components/Projections/useAIPlanProjectionsUtils'

interface AIPlanPageParams {
  aiPlanId: string
}

const AIPlanPage: FC = () => {
  const history = useHistory()
  const dispatch = useDispatch()
  const { currentAccount, currentOrganization } = useSelector((state: RootState) => state.app)
  const editMode = useSelector(selectAIPlanEditMode)
  const { aiPlanId } = useParams<AIPlanPageParams>()

  const [selectedTab, setSelectedTab] = useState('executive-summary')
  const [isEditNameFlyoutOpen, setIsEditNameFlyoutOpen] = useState(false)
  const [aiEditFlyoutState, setAiEditFlyoutState] = useState<AIEditFlyoutState | null>(null)
  const [editTitleFlyoutState, setEditTitleFlyoutState] = useState<{ currentTitle: string; titleKey?: string } | null>(null)
  const [isClientPreviewTabsFlyoutOpen, setIsClientPreviewTabsFlyoutOpen] = useState(false)
  const [showProgressModal, setShowProgressModal] = useState(false)
  const [showBrandingModal, setShowBrandingModal] = useState(false)
  const [brandingImages, setBrandingImages] = useState<BrandingImages>({ agencyLogo: null, clientLogo: null, isDirect: false })
  const [isGeneratingMarketAnalysisPdf, setIsGeneratingMarketAnalysisPdf] = useState(false)
  const [addingMarketAnalysisCustomSectionPosition, setAddingMarketAnalysisCustomSectionPosition] = useState<MarketAnalysisCustomSectionPosition | null>(null)
  const [editingMarketAnalysisCustomSection, setEditingMarketAnalysisCustomSection] = useState<{ position: MarketAnalysisCustomSectionPosition; index: number } | null>(null)
  const [deletingMarketAnalysisCustomSection, setDeletingMarketAnalysisCustomSection] = useState<{ position: MarketAnalysisCustomSectionPosition; index: number } | null>(null)

  const ganttChartRef = useRef<HTMLDivElement>(null)
  const marketAnalysisPrintRef = useRef<HTMLDivElement>(null)
  const deliveryMetricsRef = useRef<HTMLDivElement>(null)
  const connectedTvRef = useRef<HTMLDivElement>(null)
  const internetDisplayRef = useRef<HTMLDivElement>(null)
  const targetedHouseholdsRef = useRef<HTMLDivElement>(null)
  const billboardLocationsRef = useRef<HTMLDivElement>(null)
  const targetedHouseholdMapRef = useRef<any>(null)
  const billboardMapRef = useRef<any>(null)
  const mediaplanRef = useRef<AIPlanMediaPlanRef>(null)

  const { data: aiPlan, isFetching: isPlanFetching, isLoading: isPlanLoading, error: planError } = useGetAIPlanQuery({ aiPlanId }, { skip: !aiPlanId })
  const { data: stub, isFetching: isStubFetching, isLoading: isStubLoading } = useGetAIPlanStubQuery({ aiPlanId }, { skip: !aiPlanId })
  const { data: executiveSummarySection } = useGetAIPlanExecutiveSummarySectionQuery({ aiPlanId }, { skip: !aiPlanId })
  const { data: personasSection } = useGetAIPlanPersonasSectionQuery({ aiPlanId }, { skip: !aiPlanId })

  const billboardZipCodes = useAIPlanBillboardZipCodes(stub)
  const householdZipCodes = useAIPlanHouseholdZipCodes(stub)

  useGetBillboardsByZipCodesQuery({ zipCodes: billboardZipCodes }, { skip: billboardZipCodes.length === 0 })
  const { timedOutWithoutData: censusDataTimedOut } = useHouseholdCensusData(householdZipCodes)
  const staffPdfDisabledReason = censusDataTimedOut ? STAFF_PDF_CENSUS_UNAVAILABLE_TOOLTIP : undefined

  const [editName, { isLoading: isEditingName }] = useAiPlanEditNameMutation()
  const [editMarketAnalysisSection] = useAiPlanEditMarketAnalysisSectionMutation()
  const [addCompetitorProfile] = useAddAIPlanCompetitorProfileMutation()
  const [editCompetitorProfile] = useEditAIPlanCompetitorProfileMutation()
  const [removeCompetitorProfile] = useRemoveAIPlanCompetitorProfileMutation()
  const [addGeographicCluster] = useAddAIPlanGeographicClusterMutation()
  const [editGeographicCluster] = useEditAIPlanGeographicClusterMutation()
  const [removeGeographicCluster] = useRemoveAIPlanGeographicClusterMutation()
  const [addMarketChallenge] = useAddAIPlanMarketChallengeMutation()
  const [editMarketChallenge] = useEditAIPlanMarketChallengeMutation()
  const [removeMarketChallenge] = useRemoveAIPlanMarketChallengeMutation()
  const [aiEditAIPlan] = useAiEditAIPlanMutation()
  const [createPublicId] = useCreateAIPlanPublicIdMutation()
  const [updateClientPreviewTabs, { isLoading: isUpdatingClientPreviewTabs }] = useUpdateAIPlanClientPreviewTabsMutation()
  const { data: marketAnalysisSection } = useGetAIPlanMarketAnalysisSectionQuery({ aiPlanId }, { skip: !aiPlanId })
  const [updateMarketAnalysisSectionTitle, { isLoading: isUpdatingMarketAnalysisSectionTitle }] = useUpdateAIPlanMarketAnalysisSectionTitleMutation()
  const [addMarketAnalysisCustomSection, { isLoading: isAddingMarketAnalysisCustomSection }] = useAddAIPlanMarketAnalysisCustomSectionMutation()
  const [updateMarketAnalysisCustomSection, { isLoading: isUpdatingMarketAnalysisCustomSection }] = useUpdateAIPlanMarketAnalysisCustomSectionMutation()
  const [deleteMarketAnalysisCustomSection, { isLoading: isDeletingMarketAnalysisCustomSection }] = useDeleteAIPlanMarketAnalysisCustomSectionMutation()
  const [updateMarketAnalysisIntroduction, { isLoading: isUpdatingMarketAnalysisIntroduction }] = useUpdateAIPlanMarketAnalysisIntroductionSectionMutation()
  const [hideMarketAnalysisIntroduction] = useHideAIPlanMarketAnalysisIntroductionSectionMutation()

  const [isEditingMarketAnalysisIntroduction, setIsEditingMarketAnalysisIntroduction] = useState(false)
  const [isDeletingMarketAnalysisIntroduction, setIsDeletingMarketAnalysisIntroduction] = useState(false)

  const { submit: submitAIEdit, isSubmitting: isAIEditSubmitting } = useAIEdit({
    mutationFn: aiEditAIPlan,
    entityId: aiPlanId,
    onSuccess: () => setAiEditFlyoutState(null)
  })

  const tabRefs = {
    ganttChartRef,
    marketAnalysisPrintRef,
    projectionRefs: {
      deliveryMetricsRef,
      connectedTvRef,
      internetDisplayRef,
      targetedHouseholdsRef,
      billboardLocationsRef
    },
    projectionMapRefs: {
      targetedHouseholdMapRef,
      billboardMapRef
    },
    mediaplanRef
  }

  const combinedPdfHook = useAIPlanCombinedPdfGeneration({
    aiPlan: aiPlan!,
    aiPlanStub: stub!,
    executiveSummarySection,
    personasSection,
    onTabChange: (tabId: string) => setSelectedTab(tabId),
    tabRefs
  })
  const { progress: pdfProgress, error: pdfError, generateAllPdfs, clearError: clearPdfError } = combinedPdfHook

  const onClientPreviewLinkClick = async () => {
    if (!aiPlan?.id) return

    let publicId = aiPlan.publicId

    if (!publicId) {
      const updatedPlan = await createPublicId({ aiPlanId: aiPlan.id }).unwrap()
      publicId = updatedPlan.publicId
    }

    if (publicId) {
      window.open(`${config.campaignPreviewUrl}b/${publicId}`, '_blank')
    }
  }

  const handleSaveClientPreviewTabs = async (settings: { showExecutiveSummary: boolean; showMarketAnalysis: boolean; showPersonas: boolean; showMediaPlan: boolean; showProjections: boolean }) => {
    if (!aiPlan?.id) return
    await updateClientPreviewTabs({
      aiPlanId: aiPlan.id,
      ...settings
    })
    setIsClientPreviewTabsFlyoutOpen(false)
  }

  const onAllStaffPdfsClick = () => {
    if (!aiPlan || !stub || staffPdfDisabledReason) return
    setShowBrandingModal(true)
  }

  const onBrandingConfirm = async (images: BrandingImages) => {
    setBrandingImages(images)
    setShowBrandingModal(false)
    setShowProgressModal(true)
    try {
      await generateAllPdfs(true, images.agencyLogo, images.clientLogo, images.isDirect)
    } catch {
    }
  }

  const onCloseProgressModal = () => {
    setShowProgressModal(false)
    clearPdfError()
  }

  const onRetryPdfGeneration = async () => {
    setShowProgressModal(true)
    try {
      await generateAllPdfs(true, brandingImages.agencyLogo, brandingImages.clientLogo, brandingImages.isDirect)
    } catch {
    }
  }

  const handleMarketAnalysisPdfDownload = async () => {
    if (!aiPlan || !stub) return
    try {
      setIsGeneratingMarketAnalysisPdf(true)
      const context: AIPlanPdfGenerationContext = {
        aiPlan,
        aiPlanStub: stub,
        organizationName: currentOrganization?.name || '',
        accountName: currentAccount?.name || ''
      }
      const result = await generateAIPlanMarketAnalysisPdf(false, context, marketAnalysisPrintRef)
      if (result.success && result.blob) {
        const url = URL.createObjectURL(result.blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `${aiPlan.name} - Market Analysis.pdf`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(url)
      }
    } finally {
      setIsGeneratingMarketAnalysisPdf(false)
    }
  }

  useEffect(() => {
    if (!currentAccount || !aiPlan) return
    if (currentAccount.id !== aiPlan.accountId) {
      history.push('/ai/plans')
    }
  }, [currentAccount, aiPlan, history])

  useEffect(() => {
    return () => {
      dispatch(resetEditMode())
    }
  }, [dispatch])

  const handleEditName = async (name: string) => {
    if (!currentOrganization || !currentAccount || !aiPlan) return
    await editName({ organizationId: currentOrganization.id, accountId: currentAccount.id, aiPlanId: aiPlan.id, name })
  }

  const onSectionEdit = (sectionName: string, fieldKey: string, value: string) => {
    if (!aiPlan) return
    editMarketAnalysisSection({ aiPlanId: aiPlan.id, sectionName, data: { [fieldKey]: value } })
  }

  const handleSaveMarketAnalysisCustomSection = async (title: string, content: string, showTitle: boolean) => {
    if (!aiPlan) return
    if (addingMarketAnalysisCustomSectionPosition) {
      const existingSections = marketAnalysisSection?.[addingMarketAnalysisCustomSectionPosition] || []
      await addMarketAnalysisCustomSection({
        aiPlanId: aiPlan.id,
        position: addingMarketAnalysisCustomSectionPosition,
        existingSections,
        title,
        content,
        showTitle
      })
      setAddingMarketAnalysisCustomSectionPosition(null)
    } else if (editingMarketAnalysisCustomSection) {
      const existingSections = marketAnalysisSection?.[editingMarketAnalysisCustomSection.position] || []
      await updateMarketAnalysisCustomSection({
        aiPlanId: aiPlan.id,
        position: editingMarketAnalysisCustomSection.position,
        existingSections,
        sectionIndex: editingMarketAnalysisCustomSection.index,
        title,
        content,
        showTitle
      })
      setEditingMarketAnalysisCustomSection(null)
    }
  }

  const confirmDeleteMarketAnalysisCustomSection = async () => {
    if (!deletingMarketAnalysisCustomSection || !aiPlan) return
    const existingSections = marketAnalysisSection?.[deletingMarketAnalysisCustomSection.position] || []
    await deleteMarketAnalysisCustomSection({
      aiPlanId: aiPlan.id,
      position: deletingMarketAnalysisCustomSection.position,
      existingSections,
      sectionIndex: deletingMarketAnalysisCustomSection.index
    })
    setDeletingMarketAnalysisCustomSection(null)
  }

  if (isPlanLoading || isStubLoading || (!aiPlan && isPlanFetching)) {
    return (
      <EuiPage className='euiPage--platform'>
        <EuiPageBody>
          <EuiLoadingContent lines={10} />
        </EuiPageBody>
      </EuiPage>
    )
  }

  if (planError || !aiPlan) {
    return (
      <EuiPage className='euiPage--platform'>
        <EuiPageBody>
          <EuiEmptyPrompt title={<h2>Plan not found</h2>} body="The AI plan you're looking for doesn't exist or you don't have permission to view it." />
        </EuiPageBody>
      </EuiPage>
    )
  }

  const getTabFontSize = () => {
    return window.innerWidth > 1102 ? '16px' : '13px'
  }

  const tabs: EuiTabbedContentTab[] = [
    {
      id: 'executive-summary',
      name: <div style={{ paddingLeft: '.5rem', paddingRight: '.5rem', fontSize: getTabFontSize() }}>Executive Summary</div>,
      content: (
        <PageContent>
          <AIPlanExecutiveSummary aiPlan={aiPlan} stub={stub} isStubFetching={isStubFetching} onAllStaffPdfsClick={onAllStaffPdfsClick} staffPdfDisabledReason={staffPdfDisabledReason} onAIEditSection={(targetType, targetLabel, execSummarySections) => setAiEditFlyoutState({ targetType, targetId: '', targetLabel, execSummarySections })} ganttChartRef={ganttChartRef} />
        </PageContent>
      )
    },
    {
      id: 'market-analysis',
      name: <div style={{ paddingLeft: '.5rem', paddingRight: '.5rem', fontSize: getTabFontSize() }}>Analysis & Strategy</div>,
      content: (
        <PageContent>
          <EuiFlexGroup justifyContent='flexEnd'>
            <EuiFlexItem grow={false}>
              <EuiFlexGroup>
                <EuiFlexItem grow={false}>
                  {staffPdfDisabledReason ? (
                    <EuiToolTip content={staffPdfDisabledReason}>
                      <EuiButton size='s' color='primary' disabled>All Staff PDFs</EuiButton>
                    </EuiToolTip>
                  ) : (
                    <EuiButton size='s' color='primary' onClick={onAllStaffPdfsClick}>All Staff PDFs</EuiButton>
                  )}
                </EuiFlexItem>
                <EuiFlexItem grow={false}>
                  <EuiButton size='s' color='primary' onClick={handleMarketAnalysisPdfDownload} isLoading={isGeneratingMarketAnalysisPdf} disabled={isGeneratingMarketAnalysisPdf}>Client PDF</EuiButton>
                </EuiFlexItem>
              </EuiFlexGroup>
            </EuiFlexItem>
          </EuiFlexGroup>
          <EuiHorizontalRule />
          <div ref={marketAnalysisPrintRef} className='market-analysis-pdf-content'>
            <MarketAnalysisMarkdownRenderer content={marketAnalysisToMarkdown(aiPlan.marketAnalysis, marketAnalysisSection, aiPlan.companyName)} />
          </div>
          {!marketAnalysisSection?.introduction?.hideAll && (
            <>
              <EuiFlexGroup alignItems='center' gutterSize='s' responsive={false}>
                <EuiFlexItem grow={false}>
                  <EuiTitle size='m'>
                    <h3>{marketAnalysisSection?.introduction?.title || 'Market Analysis & Strategy'}</h3>
                  </EuiTitle>
                </EuiFlexItem>
                {editMode && (
                  <>
                    <EuiFlexItem grow={false}>
                      <EuiButtonIcon iconType='pencil' style={{ color: '#0071c2' }} onClick={() => setIsEditingMarketAnalysisIntroduction(true)} aria-label='Edit Introduction' size='s' />
                    </EuiFlexItem>
                    <EuiFlexItem grow={false}>
                      <EuiButtonIcon iconType='trash' style={{ color: '#BD271E' }} onClick={() => setIsDeletingMarketAnalysisIntroduction(true)} aria-label='Hide Introduction' size='s' />
                    </EuiFlexItem>
                  </>
                )}
              </EuiFlexGroup>
              <div style={{ paddingTop: 24 }}>
                <AIPlannerMarkdownRenderer content={marketAnalysisSection?.introduction?.content || `## ${aiPlan.companyName}\n\n---`} />
              </div>
              <EuiSpacer size='xl' />
            </>
          )}
          <StructuredMarketAnalysis
            marketAnalysis={aiPlan.marketAnalysis}
            aiMarketAnalysis={aiPlan.aiMarketAnalysis}
            onSectionEdit={onSectionEdit}
            onAIEditClick={(sectionName, fieldKey, fieldLabel) => {
              const sectionData = aiPlan.marketAnalysis?.[sectionName as keyof typeof aiPlan.marketAnalysis]
              const targetId = (sectionData as { id?: string })?.id || ''
              setAiEditFlyoutState({ targetType: 'MarketAnalysisField', targetId, sectionName, fieldKey, targetLabel: fieldLabel })
            }}
            onAISectionEditClick={(sectionName, sectionLabel) => {
              const sectionData = aiPlan.marketAnalysis?.[sectionName as keyof typeof aiPlan.marketAnalysis]
              const targetId = (sectionData as { id?: string })?.id || ''
              setAiEditFlyoutState({ targetType: 'MarketAnalysisSection', targetId, sectionName, targetLabel: sectionLabel })
            }}
            onEditTitleClick={(_sectionName, _fieldKey, currentLabel) => setEditTitleFlyoutState({ currentTitle: currentLabel })}
            onEditSectionTitleClick={(sectionName, currentTitle) => {
              const sectionToTitleKey: Record<string, string> = {
                strategicOverview: 'strategicOverviewTitle',
                industryContextAnalysis: 'industryContextAnalysisTitle',
                competitiveLandscape: 'competitiveLandscapeTitle',
                geographicOpportunities: 'geographicOpportunitiesTitle',
                seasonalAndCyclicalPatterns: 'seasonalPatternsTitle',
                coreChallengesInMarket: 'coreChallengesTitle',
                insightsSummary: 'insightsSummaryTitle'
              }
              setEditTitleFlyoutState({ currentTitle, titleKey: sectionToTitleKey[sectionName] })
            }}
            onAddCompetitorProfile={(profile: CompetitorProfile) => addCompetitorProfile({ aiPlanId: aiPlan.id, competitorProfile: profile })}
            onEditCompetitorProfile={(index: number, profile: CompetitorProfile) => editCompetitorProfile({ aiPlanId: aiPlan.id, index, competitorProfile: profile })}
            onRemoveCompetitorProfile={(index: number) => removeCompetitorProfile({ aiPlanId: aiPlan.id, index })}
            onAddGeographicCluster={(cluster: GeographicCluster) => addGeographicCluster({ aiPlanId: aiPlan.id, geographicCluster: cluster })}
            onEditGeographicCluster={(index: number, cluster: GeographicCluster) => editGeographicCluster({ aiPlanId: aiPlan.id, index, geographicCluster: cluster })}
            onRemoveGeographicCluster={(index: number) => removeGeographicCluster({ aiPlanId: aiPlan.id, index })}
            onAddMarketChallenge={(challenge: MarketChallenge) => addMarketChallenge({ aiPlanId: aiPlan.id, marketChallenge: challenge })}
            onEditMarketChallenge={(index: number, challenge: MarketChallenge) => editMarketChallenge({ aiPlanId: aiPlan.id, index, marketChallenge: challenge })}
            onRemoveMarketChallenge={(index: number) => removeMarketChallenge({ aiPlanId: aiPlan.id, index })}
            editMode={editMode}
            showBlueprintWarning
            marketAnalysisSection={marketAnalysisSection}
            onAddCustomSection={position => setAddingMarketAnalysisCustomSectionPosition(position)}
            onEditCustomSection={(position, index) => setEditingMarketAnalysisCustomSection({ position, index })}
            onDeleteCustomSection={(position, index) => setDeletingMarketAnalysisCustomSection({ position, index })}
          />
        </PageContent>
      )
    },
    {
      id: 'personas',
      name: <div style={{ paddingLeft: '.5rem', paddingRight: '.5rem', fontSize: getTabFontSize() }}>Personas</div>,
      content: (
        <PageContent>
          <AIPlanPersonas aiPlan={aiPlan} onAllStaffPdfsClick={onAllStaffPdfsClick} staffPdfDisabledReason={staffPdfDisabledReason} />
        </PageContent>
      )
    },
    {
      id: 'media-plan',
      name: <div style={{ paddingLeft: '.5rem', paddingRight: '.5rem', fontSize: getTabFontSize() }}>Media Plan</div>,
      content: (
        <PageContent>
          <AIPlanMediaPlan ref={mediaplanRef} aiPlan={aiPlan} stub={stub} isStubLoading={isStubLoading} onAllStaffPdfsClick={onAllStaffPdfsClick} staffPdfDisabledReason={staffPdfDisabledReason} />
        </PageContent>
      )
    },
    {
      id: 'projections',
      name: <div style={{ paddingLeft: '.5rem', paddingRight: '.5rem', fontSize: getTabFontSize() }}>Projections</div>,
      disabled: !stub?.campaignBuilders?.some(cb => cb.cpm),
      content: stub ? (
        <PageContent>
          <AIPlanProjections aiPlan={aiPlan} stub={stub} onAllStaffPdfsClick={onAllStaffPdfsClick} staffPdfDisabledReason={staffPdfDisabledReason} chartRefs={tabRefs.projectionRefs} mapRefs={tabRefs.projectionMapRefs} />
        </PageContent>
      ) : (
        <PageContent>
          <EuiLoadingContent lines={10} />
        </PageContent>
      )
    },
    {
      id: 'client-preview-link',
      name: (
        <span className='euiFlexGroup euiFlexGroup--gutterSmall euiFlexGroup--alignItemsCenter euiFlexGroup--directionRow' style={{ margin: -2, fontSize: getTabFontSize() }}>
          <div className='euiFlexItem euiFlexItem--flexGrowZero' style={{ paddingLeft: '.5rem', paddingRight: '.5rem', margin: 2 }}>
            Client Preview Link
          </div>
          <EuiButtonIcon
            className='euiFlexItem euiFlexItem--flexGrowZero'
            style={{ margin: 2 }}
            iconType='pencil'
            onClick={(e: any) => {
              e.stopPropagation()
              setIsClientPreviewTabsFlyoutOpen(true)
            }}
            aria-label='Edit Campaign Preview Tabs'
            size='s'
          />
        </span>
      ),
      content: (
        <PageContent>
          <EuiLoadingContent lines={10} />
        </PageContent>
      )
    }
  ]

  return (
    <React.Fragment>
      <Helmet>
        <title>AI Plan - {aiPlan.name}</title>
      </Helmet>

      <EuiPage className='euiPage--platform'>
        <EuiPageBody style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - var(--euiHeaderHeightCompensation))' }}>
          <AIPlanHeader planName={aiPlan.name} companyName={aiPlan.companyName} onEditNameClick={() => setIsEditNameFlyoutOpen(true)} />

          <EuiPanel>
            <EuiSpacer size='m' />
            <EuiTabbedContent
              tabs={tabs}
              selectedTab={tabs.find(tab => tab.id === selectedTab) || tabs[0]}
              onTabClick={tab => {
                if (tab.id === 'client-preview-link') {
                  onClientPreviewLinkClick()
                  return
                }
                dispatch(resetEditMode())
                setSelectedTab(tab.id)
              }}
            />
          </EuiPanel>
        </EuiPageBody>
      </EuiPage>

      {isEditNameFlyoutOpen && <EditAIPlanNameFlyout currentName={aiPlan.name} onSave={handleEditName} onClose={() => setIsEditNameFlyoutOpen(false)} isLoading={isEditingName} />}

      {aiEditFlyoutState && (
        <AIEditPromptFlyout
          targetType={aiEditFlyoutState.targetType}
          targetLabel={aiEditFlyoutState.targetLabel}
          promptLabel={aiEditFlyoutState.promptLabel}
          promptPlaceholder={aiEditFlyoutState.promptPlaceholder}
          onSubmit={(prompt: string, _constraints?: unknown, deepThink?: boolean) => submitAIEdit(prompt, aiEditFlyoutState, deepThink)}
          onClose={() => setAiEditFlyoutState(null)}
          isSubmitting={isAIEditSubmitting}
          showBlueprintWarning={!aiEditFlyoutState.targetType.startsWith('ExecSummary')}
        />
      )}

      {editTitleFlyoutState && (
        <EditTitleFlyout
          currentTitle={editTitleFlyoutState.currentTitle}
          onSave={async (newTitle: string) => {
            if (editTitleFlyoutState.titleKey) {
              await updateMarketAnalysisSectionTitle({ aiPlanId: aiPlan.id, titleKey: editTitleFlyoutState.titleKey, titleValue: newTitle })
            }
            setEditTitleFlyoutState(null)
          }}
          onClose={() => setEditTitleFlyoutState(null)}
          isSaving={isUpdatingMarketAnalysisSectionTitle}
          showBlueprintWarning
        />
      )}

      {isClientPreviewTabsFlyoutOpen && (
        <AIPlanClientPreviewTabsFlyout
          currentSettings={{
            showExecutiveSummary: aiPlan.clientPreviewShowExecutiveSummary ?? true,
            showMarketAnalysis: aiPlan.clientPreviewShowMarketAnalysis ?? true,
            showPersonas: aiPlan.clientPreviewShowPersonas ?? true,
            showMediaPlan: aiPlan.clientPreviewShowMediaPlan ?? true,
            showProjections: aiPlan.clientPreviewShowProjections ?? true
          }}
          onSave={handleSaveClientPreviewTabs}
          onClose={() => setIsClientPreviewTabsFlyoutOpen(false)}
          isLoading={isUpdatingClientPreviewTabs}
        />
      )}

      {(addingMarketAnalysisCustomSectionPosition || editingMarketAnalysisCustomSection) && (
        <EditCustomSectionFlyout
          section={editingMarketAnalysisCustomSection ? (marketAnalysisSection as any)?.[editingMarketAnalysisCustomSection.position]?.[editingMarketAnalysisCustomSection.index] : undefined}
          defaultTitle=''
          defaultContent={MARKET_ANALYSIS_CUSTOM_SECTION_EXAMPLE_CONTENT}
          defaultShowTitle={true}
          flyoutTitle={addingMarketAnalysisCustomSectionPosition ? 'Add Custom Section' : 'Edit Custom Section'}
          onSave={handleSaveMarketAnalysisCustomSection}
          onClose={() => { setAddingMarketAnalysisCustomSectionPosition(null); setEditingMarketAnalysisCustomSection(null) }}
          isLoading={isAddingMarketAnalysisCustomSection || isUpdatingMarketAnalysisCustomSection}
          hideTitleToggle
          useMarkdownEditor
          previewRenderer={AIPlannerMarkdownRenderer}
          showBlueprintWarning
        />
      )}

      {deletingMarketAnalysisCustomSection && <AcConfirmModal message='Are you sure you want to delete this custom section?' title='Delete Custom Section' onCancel={() => setDeletingMarketAnalysisCustomSection(null)} onConfirm={confirmDeleteMarketAnalysisCustomSection} cancelButtonText='Cancel' confirmButtonText='Delete' buttonColor='primary' isLoading={isDeletingMarketAnalysisCustomSection} confirmButtonDisabled={isDeletingMarketAnalysisCustomSection} />}

      {isEditingMarketAnalysisIntroduction && (
        <EditCustomSectionFlyout
          section={marketAnalysisSection?.introduction}
          defaultTitle='Market Analysis & Strategy'
          defaultContent={`## ${aiPlan.companyName}\n\n---`}
          defaultShowTitle={true}
          flyoutTitle='Edit Introduction'
          onSave={async (title: string, content: string, showTitle: boolean) => {
            await updateMarketAnalysisIntroduction({ aiPlanId: aiPlan.id, title, content, showTitle })
            setIsEditingMarketAnalysisIntroduction(false)
          }}
          onClose={() => setIsEditingMarketAnalysisIntroduction(false)}
          isLoading={isUpdatingMarketAnalysisIntroduction}
          useMarkdownEditor
          previewRenderer={AIPlannerMarkdownRenderer}
          showBlueprintWarning
        />
      )}

      {isDeletingMarketAnalysisIntroduction && <AcConfirmModal message='Are you sure you want to hide this introduction?' title='Hide Introduction' onCancel={() => setIsDeletingMarketAnalysisIntroduction(false)} onConfirm={async () => { await hideMarketAnalysisIntroduction({ aiPlanId: aiPlan.id }); setIsDeletingMarketAnalysisIntroduction(false) }} cancelButtonText='Cancel' confirmButtonText='Hide' buttonColor='primary' />}

      {showBrandingModal && <BrandingUploadModal initialImages={brandingImages} onConfirm={onBrandingConfirm} onCancel={() => setShowBrandingModal(false)} />}

      <MultiTabPdfProgressModal
        isStaffPdf={true}
        isVisible={showProgressModal}
        progress={pdfProgress}
        error={pdfError}
        onClose={onCloseProgressModal}
        onRetry={onRetryPdfGeneration}
        closeAutomatically
      />
    </React.Fragment>
  )
}

export default AIPlanPage
